import BulkProjectActions from "@app/home/bulk-project-actions/BulkProjectActions";
import { ResourceService, ResourceType } from "@app/resources";
import ResourceUpdateModel from "@app/resources/resourceUpdateModel";
import { AccordionContainer, Accordion, BreadcrumbTemplateEnum, Breadcrumbs, FilterCriteria, FilterCriteriaModel, FilterCriteriaOnChangeResponse, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection, breadcrumbTemplates } from "@app/shared";
import { formatDate } from "@app/shared";
import { ColumnConfig, WafflesFilterType } from "@app/shared/waffles";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import { Project, ProjectService } from "../projects";
import { Unauthorized } from "../shared/ErrorPages";
import { QESAlert, QESButton, QESMultiSelect, QESSelect } from "@app/shared/qes-standards";
import { MemberSetTypeEnum, MemberSetVersionStatus } from "./enumerations";
import MemberSetArchiveFlyout from "./MemberSetArchiveFlyout";
import MemberSetCountByCountyTable from "./MemberSetCountByCountyTable";
import MemberSetCountByStateTable from "./MemberSetCountByStateTable";
import MemberSetEditResourceFlyout from "./MemberSetEditResourceFlyout";
import { MemberSetModel, Version } from "./memberSetModels";
import MemberSetPreviewTable from "./MemberSetPreviewTable";
import MemberSetService from "./memberSetService";
import { FlyoutLink } from "@app/shared/Flyouts";
import { MemberSetOverallSummary } from "./MemberSetOverallSummary";
import moment from "moment";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { JSX } from "react";

type ProjectModule = "Summary Information" | "Preview";

interface MemberSetDetailState {
  activateVersionButtonDisabled: boolean;
  activeModule: ProjectModule;
  associatedProjects: Project[];
  availableFilteredMemberSets: MemberSetModel[];
  compareMode: boolean;
  error: string;
  hasFilterChanged: boolean;
  initialFiltersForSelectedVersion: FilterCriteriaModel[];
  loadingResourceIds: boolean;
  memberCount: number;
  memberSet: MemberSetModel;
  processingFilters: boolean;
  selectedCompareVersion: Version;
  selectedCompareVersionKey: number;
  selectedFilteredMemberSetActivateOptions: MemberSetModel[];
  selectedVersion: Version;
  selectedVersionKey: number;
  showActivationButtons: boolean;
  sourceMemberSetInfo: string;
  usedColumns: string[];
  userIsOwner: boolean;
}

interface MemberSetRouteProps {
  memberSetId: string;
}
type MemberSetDetailProps = RouteComponentProps<MemberSetRouteProps> & UserContextProps;

class MemberSetDetailContent extends React.Component<MemberSetDetailProps, MemberSetDetailState> {

  private get memberSetId() {
    return Number(this.props.match.params.memberSetId);
  }
  private readonly memberSetService = new MemberSetService();
  private readonly resourceService = new ResourceService();
  private readonly projectService = new ProjectService();
  private readonly userIsQuestAdmin: boolean = this.props.user.roles.includes(Roles.QUEST_ADMIN);
  private readonly userIsClientAdmin: boolean = this.props.user.roles.includes(Roles.CLIENT_ADMIN);
  private readonly userIsResourceAdmin: boolean = this.props.user.roles.includes(Roles.RESOURCE_ADMIN);

  constructor(props: MemberSetDetailProps) {
    super(props);

    this.state = {
      activateVersionButtonDisabled: true,
      activeModule: "Summary Information",
      associatedProjects: [],
      availableFilteredMemberSets: [],
      compareMode: false,
      error: "",
      hasFilterChanged: false,
      initialFiltersForSelectedVersion: [],
      loadingResourceIds: true,
      memberCount: 0,
      memberSet: null,
      processingFilters: false,
      selectedCompareVersion: null,
      selectedCompareVersionKey: 0,
      selectedFilteredMemberSetActivateOptions: [],
      selectedVersion: null,
      selectedVersionKey: null,
      showActivationButtons: false,
      sourceMemberSetInfo: "",
      usedColumns: [],
      userIsOwner: false,
    };
  }

  public async componentDidMount() {
    await this.fetchMemberSet();
  }

  public handleVersionChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const newVersionKey = Number(event.target.value);
    const newVersion = this.state.memberSet.memberSetVersions.filter((option) =>
      option.memberSetVersionId === newVersionKey).pop();
    const versionStatus = newVersion.memberSetVersionStatus;
    const newFilters = await this.fetchFiltersForSelectedVersion(newVersion.memberSetVersionId);
    const showActivationButtons = this.canBeModified(this.userIsQuestAdmin, this.userIsResourceAdmin, this.state.memberSet.resource.userId === this.props.user.userId, this.state.memberSet.resource.isShared, this.state.memberSet.resource.isSystemResource) &&
      versionStatus !== MemberSetVersionStatus.Activated;

    this.setState(() => ({
      activateVersionButtonDisabled: this.canBeActivated(this.state.memberSet, newVersion, newFilters),
      initialFiltersForSelectedVersion: newFilters,
      selectedVersion: newVersion,
      selectedVersionKey: newVersionKey,
      showActivationButtons: showActivationButtons,
    }));
  }

  public handleCompareVersionChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newCompareVersionKey = event.target.value === "" ? 0 : Number(event.target.value);
    const newCompareVersion = event.target.value === "" ? null : this.state.memberSet.memberSetVersions.filter((option) =>
      option.memberSetVersionId === newCompareVersionKey).pop();

    const compareMode = event.target.value === "" ? false : true;

    this.setActiveModule("Summary Information");
    this.setState(() => ({
      compareMode,
      selectedCompareVersion: newCompareVersion,
      selectedCompareVersionKey: newCompareVersionKey,
    }));
  }

  public handleActivateVersionButtonClick = async (): Promise<void> => {
    this.memberSetService.activateMemberSetVersion(this.state.memberSet.memberSetId, this.state.selectedVersionKey, this.state.selectedFilteredMemberSetActivateOptions.map(x => x.memberSetId)).then(() => {
      //reload memberset since it should be updated
      this.fetchMemberSet();
      toast(`Your member population was successfully activated.`, {
        containerId: "z-index-1030",
        type: "success",
      });
    }, () => {
      this.setState(() => ({
        error: "We are unable to activate this member population at this time. Please try again later.",
      }));
    });

  }

  public handleArchiveMemberSetSubmit = async (): Promise<void> => {
    this.memberSetService.archiveMemberSet(this.state.memberSet.memberSetId).then(() => {
      this.fetchMemberSet();
      toast(`Your member population is being archived.`, {
        containerId: "z-index-1030",
        type: "success",
      });
    }, () => {
      this.setState(() => ({
        error: "We are unable to archive this member population at this time. Please try again later.",
      }));
    });
  }

  public handleEditMemberSetSubmit = async (resourceModel: ResourceUpdateModel): Promise<void> => {
    this.resourceService.updateResource(this.state.memberSet.memberSetId, resourceModel).then(() => {
      this.fetchMemberSet();
      toast(`Your member population was successfully edited.`, {
        containerId: "z-index-1030",
        type: "success",
      });
    }, () => {
      this.setState(() => ({
        error: "We are unable to process these changes at this time. Please try again later.",
      }));
    });

  }

  public getProjects = (): JSX.Element => {
    return this.getBulkProjectActionsContent();
  }
  public getMemberSetArchivePanelContent = (): JSX.Element => {
    return <MemberSetArchiveFlyout onSubmit={this.handleArchiveMemberSetSubmit} loadingProjects={this.state.loadingResourceIds} associatedProjects={this.state.associatedProjects} />;
  }

  public getEditMemberSetSidePanelContent = (): JSX.Element => {
    return <MemberSetEditResourceFlyout onSubmit={this.handleEditMemberSetSubmit} memberSet={this.state.memberSet} />;
  }

  public fetchMemberSet = async (): Promise<void> => {
    try {
      const memberSet = await this.memberSetService.getById(this.memberSetId, false, true);
      await this.fetchMemberSetResponse(memberSet);
    } catch {
      this.setState({ error: "unauthorized" });
    }
  }

  public fetchSourceMemberSet = async (sourceId: number, sourceVersionId: number): Promise<void> => {
    try {
      const sourceMemberSet = await this.memberSetService.getById(sourceId, false, true);
      const sourceVersion = sourceMemberSet.memberSetVersions.filter((x) => x.memberSetVersionId === sourceVersionId)[0];
      this.setState({ sourceMemberSetInfo: sourceMemberSet.resource.name + " - " + formatDate(sourceVersion.dateLastUpdated) })
    } catch {
      this.setState({ sourceMemberSetInfo: "" });
    }
  }

  public fetchMemberSetResponse = async (response: MemberSetModel): Promise<void> => {
    const hasVersion = (response.memberSetVersions && response.memberSetVersions.length > 0);
    const memberSetVersion = hasVersion ? response.memberSetVersions[response.memberSetVersions.length - 1] : null;
    const isUserOwner = response.resource.userId === this.props.user.userId;
    const showActivationButtons = this.canBeModified(this.userIsQuestAdmin, this.userIsResourceAdmin, isUserOwner, response.resource.isShared, response.resource.isSystemResource) &&
      hasVersion && memberSetVersion.memberSetVersionStatus !== MemberSetVersionStatus.Activated;

    let filteredMemberSets: MemberSetModel[] = [];
    if (showActivationButtons && response.resource.resourceType === ResourceType.MemberSet) {
      filteredMemberSets = await this.memberSetService.getFilteredMemberSets(response.memberSetId);
      filteredMemberSets = filteredMemberSets.filter(x => !x.resource.dateArchived);
    }

    this.setState(() => ({
      activateVersionButtonDisabled: this.canBeActivated(response, memberSetVersion, this.state.initialFiltersForSelectedVersion),
      availableFilteredMemberSets: filteredMemberSets,
      memberSet: response,
      selectedFilteredMemberSetActivateOptions: filteredMemberSets,
      selectedVersion: memberSetVersion,
      selectedVersionKey: hasVersion ? memberSetVersion.memberSetVersionId : null,
      showActivationButtons: showActivationButtons,
      userIsOwner: isUserOwner,
    }), () => {
      this.buildFilterCols();
      this.getProjectIds();
    });

    if (!!response.sourceMemberSetId) {
      const latestVersion = response.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];
      await this.fetchSourceMemberSet(response.sourceMemberSetId, latestVersion.sourceMemberSetVersionId);
      this.buildFilterCols();
    }

  }

  public updateFilteredMemberSetActivateOptions = (response: MultiselectOnChangeResponse) => {
    const selectedMemberSets = response.checkedKeys;
    const newMemberSets = selectedMemberSets.map((memberSetId) => {
      const matchedRoleModel = this.state.availableFilteredMemberSets.find((x) => x.resource.resourceId.toString() === memberSetId);
      return matchedRoleModel;
    });
    this.setState({ selectedFilteredMemberSetActivateOptions: newMemberSets });
  }

  public getMemberSetMultiSelectOptions = (memberSets: MemberSetModel[]) => {
    const options: { [key: string]: MultiselectItem } = {};

    if (!!memberSets) {
      memberSets.forEach((memberSet) => Object.assign(options,
        {
          [memberSet.resource.resourceId]: {
            currentState: this.state.selectedFilteredMemberSetActivateOptions.some((x) => x.resource.resourceId === memberSet.resource.resourceId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: memberSet.resource.name,
          }
        }));
    }

    return options;
  }

  public render() {
    if ((this.state.memberSet && this.state.memberSet.resource.isSystemResource && !this.userIsQuestAdmin && !this.userIsClientAdmin) || this.state.error === "unauthorized") {
      return <Unauthorized />;
    }

    let membersetVersionsDropdown: JSX.Element;
    let membersetCompareVersionsDropdown: JSX.Element;

    if (this.state.memberSet && this.state.memberSet.memberSetVersions && this.state.memberSet.memberSetVersions.length > 0) {
      //render full select box
      membersetVersionsDropdown = (
        <QESSelect
          label={"Version:\xa0"}
          value={this.state.selectedVersionKey}
          testId="member-set-detail__member-set-version-list-select"
          onChange={(e) => this.handleVersionChange(e)}
        >
          {
            this.state.memberSet.memberSetVersions.sort((a, b) => a.memberSetVersionId < b.memberSetVersionId ? 1 : -1).map((option) => (
              <option
                key={option.memberSetVersionId}
                value={option.memberSetVersionId}
                disabled={option.dateLastUpdated === this.state.selectedVersion.dateLastUpdated}
              >
                {`${moment.utc(option.dateLastUpdated).local().format("MMM DD, YYYY h:mm:ss A")}`}
              </option>
            ))
          }
        </QESSelect>
      );
    } else {
      //render mock select
      membersetVersionsDropdown = <QESSelect label="Version: " testId="member-set-detail__member-set-version-list-select" placeholder="Select a Member Set Version" defaultValue="" />;
    }

    if (this.state.memberSet && this.state.memberSet.memberSetVersions && this.state.memberSet.memberSetVersions.length > 1) {
      membersetCompareVersionsDropdown = (
        <QESSelect
          label={"Compare:\xa0"}
          value={this.state.selectedCompareVersionKey}
          testId="member-set-detail__member-set-compare-version-list-select"
          onChange={(e) => this.handleCompareVersionChange(e)}
          containerClassName="col-6"
        >
          {
            [<option value="" key="">None</option>].concat(
              this.state.memberSet.memberSetVersions.sort((a, b) => a.memberSetVersionId < b.memberSetVersionId ? 1 : -1).map((option) => (
                option.memberSetVersionId !== this.state.selectedVersionKey ?
                  (<option key={option.memberSetVersionId} value={option.memberSetVersionId}>{`${moment.utc(option.dateLastUpdated).local().format("MMM DD, YYYY h:mm:ss A")}`}</option>) : null)))
          }
        </QESSelect>
      );
    } else {
      membersetCompareVersionsDropdown = <QESSelect label={"Compare:\xa0"} placeholder="None" testId="member-set-detail__member-set-compare-version-list-select" defaultValue="" containerClassName="col-6" />;
    }

    const description = (
      <div className="row ml-3 mt-3">
        <p>
          {(this.state.memberSet ? this.state.memberSet.resource.description : null)}
        </p>
      </div>
    );

    let activationAlert: JSX.Element = null;

    if (this.state.activateVersionButtonDisabled) {
      activationAlert = (
        <div className="col-12 ml-3 mt-3">
          <QESAlert key="ActivateAlert" type="warning" persist={true}>
            <div className="mt-2 pull-left"><strong>Warning!</strong> This network version has not been activated.</div>
          </QESAlert>
        </div>);
    } else {
      const filteredProvidersMultiSelect = (
        <>
          <QESMultiSelect
            containerClassName="col-8"
            label="These associated filtered networks can also be activated"
            onChange={this.updateFilteredMemberSetActivateOptions}
            options={this.getMemberSetMultiSelectOptions(this.state.availableFilteredMemberSets)}
            testId="provider-detail__activated-filtered-providers"
          />
          <div className="mb-3">
            <QESButton
              qesTestId="provider-detail__select-all-filtered-providers"
              qesType="secondary"
              onClick={() => this.setState({ selectedFilteredMemberSetActivateOptions: this.state.availableFilteredMemberSets })}>
              Select All
            </QESButton>
            <QESButton
              qesTestId="provider-detail__clear-all-filtered-providers"
              qesType="secondary"
              onClick={() => this.setState({ selectedFilteredMemberSetActivateOptions: [] })}>
              Clear All
            </QESButton>
          </div>
        </>
      );

      activationAlert = (
        <div key="ActivateAlert" className="col-12 ml-3 mt-3">
          <QESAlert type="warning" persist={true}>
            <div className="mt-1">
              <div className="pull-left"><strong>Warning!</strong> This network version has not been activated.</div>
              <FlyoutLink
                content={{
                  body: this.getBulkProjectActionsContent(),
                  title: "Activate and Run Projects"
                }}
                testId="member-set-detail__activate-flyout"
                classNames="qes-button-secondary ml-3 pull-right"
                disabled={this.state.activateVersionButtonDisabled}
              >
                Activate and Run Projects
              </FlyoutLink>
              <QESButton qesTestId="provider-detail__activate" qesType="secondary" className="pull-right btn-light-gray" onClick={this.handleActivateVersionButtonClick} > Activate </QESButton>
            </div>
          </QESAlert>
          {!!this.state.availableFilteredMemberSets && this.state.availableFilteredMemberSets.length > 0 ?
            <>{filteredProvidersMultiSelect}</> : null
          }
        </div>);
    }

    const summaryInformation = (
      <div className="col-12">
        <AccordionContainer>
          <Accordion title="Members by State" testId="member-set-detail__members-by-state-accordion">
            <MemberSetCountByStateTable selectedVersion={this.state.selectedVersion} comparedVersion={this.state.selectedCompareVersion} compareMode={this.state.compareMode} />
          </Accordion>
          <Accordion title="Members by County" testId="member-set-detail__members-by-county-accordion">
            <MemberSetCountByCountyTable selectedVersion={this.state.selectedVersion} comparedVersion={this.state.selectedCompareVersion} compareMode={this.state.compareMode} />
          </Accordion>
        </AccordionContainer>
      </div>);

    const memberPreviewTable = (
      <div className="col-12">
        <MemberSetPreviewTable memberSetVersion={this.state.selectedVersion} memberCount={this.state.memberCount} />
      </div>
    );

    const memberSetInfo = (
      (this.state.memberSet ? (
        <div className="row ml-3 mt-3">
          <dl className="d-table">
            <div className="d-table-row">
              <dt className="d-table-cell font-weight-normal">Member Population Type: </dt>
              <dd className="d-table-cell pl-2">{this.state.memberSet.memberSetType}</dd>
            </div>
            {this.state.memberSet.resource.resourceType === ResourceType.FilteredMemberSet && this.state.sourceMemberSetInfo.length > 0 && (
              <div className="d-table-row">
                <dt className="d-table-cell font-weight-normal">Source Network: </dt>
                <dd className="d-table-cell pl-2">{this.state.sourceMemberSetInfo}</dd>
              </div>
            )}
            <div className="d-table-row">
              <dt className="d-table-cell font-weight-normal">Date Created: </dt>
              <dd className="d-table-cell pl-2">{(this.state.selectedVersion.dateCreated ? formatDate(this.state.selectedVersion.dateCreated) : null)}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell font-weight-normal">Date Last Updated: </dt>
              <dd className="d-table-cell pl-2">{this.state.selectedVersion.dateLastUpdated ? formatDate(this.state.selectedVersion.dateLastUpdated) : null}</dd>
            </div>
            <div className="d-table-row">
              <dt className="d-table-cell font-weight-normal">Date Activated: </dt>
              <dd className="d-table-cell pl-2">{this.state.selectedVersion.dateActivated ? formatDate(this.state.selectedVersion.dateActivated) : null}</dd>
            </div>
          </dl>
        </div>
      ) : null)
    );

    /*only show preview on full member populations and never on system resources*/
    const navTabs = (
      <div className="row ml-3 mt-3">
        <div className="tabs-style-2">
          <ul className="nav nav-tabs">
            <li className="nav-item"><a className={this.state.activeModule === "Summary Information" ? "nav-link active" : "nav-link"} role="tab" onClick={() => this.setActiveModule("Summary Information")}>Summary Information</a></li>

            {this.state.memberSet && this.state.memberSet.memberSetType === MemberSetTypeEnum.Full && !this.state.memberSet.resource.isSystemResource && !this.state.compareMode ?
              (
                <li className="nav-item"><a className={this.state.activeModule === "Preview" ? "nav-link active" : "nav-link"} role="tab" onClick={() => this.setActiveModule("Preview")}>Preview</a></li>
              ) : null}

          </ul>
        </div>
      </div>
    );

    const breadcrumbItemsBase = breadcrumbTemplates[BreadcrumbTemplateEnum.MemberPopulations];
    const breadcrumbCurrentPage = {
      text: !!this.state.memberSet ? this.state.memberSet.resource.name : "",
    };
    const breadcrumbReturnLink = {
      link: `/member-populations/${!!this.state.memberSet ? this.state.memberSet.memberSetId : ""}/detail`,
      text: !!this.state.memberSet ? this.state.memberSet.resource.name : "",
    };

    if (this.state.memberSet) {
      const latestVersion = this.state.memberSet.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];
      const isLatestVersion = latestVersion.memberSetVersionId === this.state.selectedVersion.memberSetVersionId;
      const isModifyable = this.canBeModified(this.userIsQuestAdmin, this.userIsResourceAdmin, this.state.userIsOwner, this.state.memberSet.resource.isShared, this.state.memberSet.resource.isSystemResource);

      return (
        <Page documentTitle={`${this.state?.memberSet.resource.name || "Loading"} - Member Populations`}>
          <Breadcrumbs items={[...breadcrumbItemsBase, breadcrumbCurrentPage]} />
          <PageInner>
            {/*TODO: Implement links on this page*/}
            <PageHeader title={this.state.memberSet.resource.name}>
              <PageHeaderLeft>
                {isModifyable && !this.state.memberSet.resource.isSystemResource && (
                  <>
                    <i className="fa fa-share text-muted" />
                    <Link
                      to={{
                        pathname: `/resources/share`,
                        search: "?redirectUrl=/member-populations",
                        state: {
                          breadcrumbItems: [...breadcrumbItemsBase, breadcrumbReturnLink],
                          isSingleResource: true,
                          resourceDescription: "Member Population",
                          resourceId: this.state.memberSet.memberSetId,
                          resourceTypes: [ResourceType.MemberSet],
                        },
                      }}
                    >
                      Share
                    </Link>
                  </>
                )}
                {isModifyable && (
                  <span className="ml-2">
                    <FlyoutLink
                      content={{
                        body: this.getEditMemberSetSidePanelContent(),
                        title: `Edit ${this.state.memberSet.resource.name}`,
                      }}
                      testId="member-set-detail__edit-flyout"
                    >
                      <i className="fa fa-pencil text-muted mr-1" />
                      Edit
                    </FlyoutLink>
                  </span>
                )}
                {isModifyable && !this.state.memberSet.resource.isSystemResource && !this.state.memberSet.sourceMemberSetId && this.state.memberSet.memberSetVersions.some((x) => x.dateActivated != null) && (
                  <Link className="ml-2" to={{ pathname: "/member-populations/create-filtered", state: { sourceMemberSetId: this.state.memberSet.memberSetId } }}>
                    <i className="fa fa-filter text-muted" /> Create Filtered Member Population
                  </Link>
                )}
              </PageHeaderLeft>
              <PageHeaderRight>
                <div className="btn-group pull-right">
                  {isModifyable && !this.state.memberSet.resource.isArchived && (
                    <FlyoutLink
                      content={{
                        body: this.getMemberSetArchivePanelContent(),
                        title: `Archive ${this.state.memberSet.resource.name}`,
                      }}
                      testId="member-set-detail__archive-flyout"
                      classNames="qes-button-secondary ml-3 pl-3 pr-3"
                      size="40"
                    >
                      Archive
                    </FlyoutLink>
                  )}
                </div>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              <div className="row ml-3 mt-3 mb-1">
                {this.state.error.length > 0 && <QESAlert classNames="mb-3" type="danger" persist={true}>{this.state.error}</QESAlert>}
                <p>
                  {this.state.memberSet.resource.isPrivate ? "This is a private member population (can be administered only by you)" :
                    "This member population is not private (can be administered by you and other users authorized to administer non-private member populations)"}
                </p>
              </div>
              {memberSetInfo}
              {description}
              <div className="row ml-3 mt-3">
                <div className="col-md-6 pl-0">
                  {membersetCompareVersionsDropdown}
                </div>
                <div className="col-md-6">
                  <div className="float-right">
                    {membersetVersionsDropdown}
                  </div>
                </div>
              </div>
              {this.state.showActivationButtons && activationAlert}
            </PageSection>
            {isModifyable && !this.state.memberSet.resource.isSystemResource && (
              <PageSection>
                <div className="row ml-3 mt-3">
                  <div className="col-12">
                    {!this.state.hasFilterChanged && !!this.state.memberSet ?
                      (<MemberSetOverallSummary version={this.state.selectedVersion} compareVersion={this.state.selectedCompareVersion} onUpdate={(count: number) => this.setState({ memberCount: count })} />) : null
                    }
                    {this.state.memberSet.sourceMemberSetId && (
                      <FilterCriteria
                        fieldNames={this.state.usedColumns}
                        defaultCriteria={this.state.initialFiltersForSelectedVersion}
                        onChange={(response) => this.handleFilterChange(response)}
                        disabled={this.state.processingFilters || !isLatestVersion}
                        onSave={(response) => this.handleFilterSubmit(response)}
                      />
                    )}
                  </div>
                </div>
              </PageSection>
            )}
            <PageSection>
              {(!this.state.memberSet.resource.isSystemResource || this.userIsQuestAdmin) && (
                navTabs
              )}
              <div className="row ml-3 mt-3">
                {this.state.hasFilterChanged && this.state.memberSet.sourceMemberSetId && (
                  <QESAlert type="info" classNames="mb-3" persist={true}>Process filter criteria to update summary information.</QESAlert>
                )}
                {this.state.activeModule === "Summary Information" ? summaryInformation : this.state.activeModule === "Preview" ? memberPreviewTable : null}
              </div>
            </PageSection>
          </PageInner >
        </Page >
      );
    } else {
      return null;
    }
  }

  private canBeModified = (isQuestAdmin: boolean, isResourceAdmin: boolean, isUserOwner: boolean, sharedResource: boolean, systemResource: boolean) => {
    if (systemResource) return (isQuestAdmin || isUserOwner);
    else if (!sharedResource) return (isResourceAdmin || isUserOwner);
    return false;
  }

  private canBeActivated = (memberSet: MemberSetModel, selectedVersion: Version, filterCriteria?: FilterCriteriaModel[]) => {
    if (!memberSet.memberSetVersions)
      return true;

    const latestVersion = memberSet.memberSetVersions.sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1)[0];

    return latestVersion.memberSetVersionId !== selectedVersion.memberSetVersionId ||
      memberSet.resource.isShared ||
      (memberSet.sourceMemberSetId ? !filterCriteria || filterCriteria.length === 0 : false);
  }

  private handleFilterSubmit = (filters: FilterCriteriaModel[]) => {
    if (this.state.processingFilters) {
      return;
    }
    this.setState({
      processingFilters: true,
    }, () => {
      this.memberSetService.saveFilterCriteria(
        this.state.memberSet.memberSetId,
        this.state.selectedVersion.memberSetVersionId,
        this.state.selectedVersion.sourceMemberSetVersionId,
        this.state.memberSet.sourceMemberSetId,
        filters,
      ).then(() => {
        this.fetchMemberSet();
        this.setState({
          activateVersionButtonDisabled: (filters.length === 0),
          error: "",
          processingFilters: false,
        });
      }, () => {
        this.fetchMemberSet();
        this.setState({
          error: "We encountered a problem saving your filter. Please try again.",
          processingFilters: false,
        });
      });
    });
  }

  private handleFilterChange = (response: FilterCriteriaOnChangeResponse) => {
    this.setState({
      hasFilterChanged: response.hasChanges,
    });
  }

  private buildFilterCols = async () => {
    if (!this.state.memberSet) {
      return;
    }
    if (!this.state.memberSet.sourceMemberSetId) {
      return;
    }

    const convertFilterType = (filterType: string) => {
      switch (filterType.toLowerCase()) {
        case "int":
        case "decimal":
          return WafflesFilterType.number;
        case "string":
          return WafflesFilterType.string;
        case "bit":
        case "bool":
          return WafflesFilterType.boolean;
        case "datetime":
          return WafflesFilterType.dateTime;
      }
    };

    let columns: ColumnConfig = {};
    this.memberSetService.getMemberSetSchema(this.state.memberSet.sourceMemberSetId).then((apiColumns) => {
      apiColumns.forEach((cc) => {
        Object.assign(columns, {
          [cc.name]: {
            displayText: cc.name,
            filterType: convertFilterType(cc.dataType),
            testId: cc.name,
          },
        });
      });

      const usedCols = Object.entries(columns).filter((fc) => !fc[1].hidden).map((ffc) => ffc[0]);

      this.memberSetService.getSavedMemberSetFilters(this.state.memberSet.memberSetId).then((savedFilters) => {
        this.setState({
          activateVersionButtonDisabled: this.canBeActivated(this.state.memberSet, this.state.selectedVersion, savedFilters),
          initialFiltersForSelectedVersion: savedFilters,
          usedColumns: usedCols,
        });
      }, () => {
        this.setState({
          activateVersionButtonDisabled: true,
          initialFiltersForSelectedVersion: [],
          usedColumns: usedCols,
        });
      });
    }, () => {
      columns = {
        countySSA: {
          displayText: "County SSA",
          filterType: WafflesFilterType.string,
          testId: "county-ssa",
        },
        state: {
          displayText: "State",
          filterType: WafflesFilterType.string,
          testId: "state",
        },
        zip: {
          displayText: "Zip",
          filterType: WafflesFilterType.string,
          testId: "zip",
        },
      };
      const usedCols = Object.entries(columns).filter((fc) => !fc[1].hidden).map((ffc) => ffc[0]);

      this.memberSetService.getSavedMemberSetFilters(this.state.memberSet.memberSetId).then((savedFilters) => {
        this.setState({
          activateVersionButtonDisabled: this.canBeActivated(this.state.memberSet, this.state.selectedVersion, savedFilters),
          initialFiltersForSelectedVersion: savedFilters,
          usedColumns: usedCols,
        });
      }, () => {
        this.setState({
          activateVersionButtonDisabled: true,
          initialFiltersForSelectedVersion: [],
          usedColumns: usedCols,
        });
      });
    });
  }

  private fetchFiltersForSelectedVersion = async (versionId?: number): Promise<FilterCriteriaModel[]> => {
    if (!this.state.memberSet.sourceMemberSetId) {
      return [];
    }
    const selectedVersionId = versionId || this.state.selectedVersion.memberSetVersionId;
    const filters = await this.memberSetService.getSavedMemberSetFilters(this.state.memberSet.memberSetId, selectedVersionId);

    return filters;
  }

  private setActiveModule = (module: ProjectModule) => {
    this.setState({
      activeModule: module,
    });
  }

  private getBulkProjectActionsContent = () => {
    return (
      <BulkProjectActions
        loadingResourceIds={this.state.loadingResourceIds}
        resourceIds={this.state.associatedProjects.map((project) => {
          return project.projectId;
        })}
        onPublish={() => null}
        onSubmit={this.handleActivateVersionButtonClick}
        showPublish={false}
        showArchive={false}
        setProjectIsReadOnly={() => null}
      />
    );
  }

  private getProjectIds = async () => {
    if (!this.state.memberSet) return;
    const projects = await this.projectService.getByMemberSetId(this.state.memberSet.memberSetId);
    this.setState(() => ({
      associatedProjects: projects,
      loadingResourceIds: false,
    }));
  }
}

const MemberSetDetail = withUserContext(MemberSetDetailContent);
export default MemberSetDetail;
